import { useMemo } from 'react';
import Color from 'color';

import config from '@/config';

interface Theme {
  primary: string;
  primaryLight: string;
  secondary: string;
  secondaryLight: string;
}

const DEFAULT_THEME = {
  primary: config('/tw/colors/teal/500'),
  primaryLight: config('/tw/colors/teal/300'),
  secondary: config('/tw/colors/green/500'),
  secondaryLight: config('/tw/colors/green/300'),
} satisfies Theme;

const lightenColor = (color: string): string => {
  if (color === DEFAULT_THEME.primary) return DEFAULT_THEME.primaryLight;
  if (color === DEFAULT_THEME.secondary) return DEFAULT_THEME.secondaryLight;
  return Color(color).mix(Color('white')).hex();
};

const colorToRGBValues = (color: string): string => Color(color).array().join(' ');

type CustomThemeOptions = Partial<Pick<Theme, 'primary' | 'secondary'>> & {
  enabled?: boolean;
};

const useCustomTheme = ({ primary, secondary, enabled = true }: CustomThemeOptions = {}) => {
  return useMemo(() => {
    const theme = {
      ...DEFAULT_THEME,
      ...(enabled && primary && { primary, primaryLight: lightenColor(primary) }),
      ...(enabled && secondary && { secondary, secondaryLight: lightenColor(secondary) }),
    };

    return {
      ...theme,
      style: (
        <style
          dangerouslySetInnerHTML={{
            __html: `
              :root {
                --color-theme-primary: ${colorToRGBValues(theme.primary)};
                --color-theme-primary-light: ${colorToRGBValues(theme.primaryLight)};
                --color-theme-secondary: ${colorToRGBValues(theme.secondary)};
                --color-theme-secondary-light: ${colorToRGBValues(theme.secondaryLight)};
              }
           `,
          }}
        />
      ),
    };
  }, [enabled, primary, secondary]);
};

export default useCustomTheme;
